import { useState, useEffect, useCallback, useRef } from 'react';
import { formatDate, formatDateTime } from '../lib/dates';
import { api, ApiError, asList } from '../lib/api';
import { useT } from '../context/I18nContext';

/* ============================================================================
   WHAT A PARENT SEES

   Two things, deliberately on one page, because a parent thinks of them together:
   "where is the bus" and "I'm coming to collect".

   THE LIVE BUS is deliberately thin: a dot, a freshness, nothing else. No route
   history, no other children, no student positions — none of that exists.

   THE GATE PASS carries a code that ROTATES EVERY 30 SECONDS. A screenshot is
   dead within half a minute, which is the entire point: a static QR a stranger
   could photograph over your shoulder and reuse at the gate would be worse than
   having no system at all.
   ========================================================================== */

interface LiveBus {
  tripId: string; routeName: string; vehicleName: string | null; direction: string;
  isLive: boolean; latitude: number | null; longitude: number | null;
  accuracyMetres: number | null; positionAgeSeconds: number; startedAt: string;
}
// ⚠ The API returns `id`, not `studentId` — see PortalStudent in PortalService.
//
// This interface said `studentId`, so the field was `undefined` at runtime. On a
// <select>, an option with `value={undefined}` falls back to its TEXT, which is why
// the request carried "Aarav Shetty" where a GUID belonged and the API returned 400.
//
// TypeScript could not catch it: the response is cast from JSON, so a mismatched
// field name is an assertion, not a checked fact.
interface Child { id: string; fullName: string; admissionNumber: string; classLabel: string | null; }
interface GatePass {
  id: string; studentId: string; studentName: string; admissionNumber: string; classLabel: string | null;
  status: string; reason: string | null;
  currentCode: string; secondsUntilRotate: number;
  issuedAt: string; expiresAt: string; usedAt: string | null; gateName: string | null;
}

export function FamilySafetyPage() {
  const t = useT();
  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>{t('Safety')}</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Bus &amp; pickup</h1>
      </div>

      <LiveBusPanel />
      <GatePassPanel />
    </div>
  );
}

/* ----------------------------------------------------------------- the bus */

function LiveBusPanel() {
  const t = useT();
  const [buses, setBuses] = useState<LiveBus[] | null>(null);

  const load = useCallback(() => {
    api.get<LiveBus[]>('/api/portal/transport/live').then(setBuses).catch(() => setBuses([]));
  }, []);

  useEffect(() => {
    load();
    // Poll while the parent is watching. Cheap — one row.
    const t = setInterval(load, 10_000);
    return () => clearInterval(t);
  }, [load]);

  if (!buses) {
    return <div className="card" style={{ padding: 20, marginBottom: 20 }}><span className="sk" style={{ width: '50%' }} /></div>;
  }

  // No trip running, or the child is absent today → nothing to show. Deliberately.
  if (buses.length === 0) {
    return (
      <div className="card" style={{ padding: 20, marginBottom: 20 }}>
        <h2 style={{ fontSize: 16, fontFamily: 'var(--font-display)', marginBottom: 4 }}>{t('The bus')}</h2>
        <p style={{ fontSize: 13.5, color: 'var(--ink-2)' }}>
          No bus is running right now. You'll be able to see it once it leaves.
        </p>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 24 }}>
      {buses.map((b) => (
        <div key={b.tripId} className="card" style={{ padding: 18, marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
            <h2 style={{ fontSize: 17, fontFamily: 'var(--font-display)' }}>
              🚌 {b.routeName}
            </h2>
            {b.vehicleName && (
              <span className="pill pill-muted" style={{ fontSize: 11 }}>{b.vehicleName}</span>
            )}
            <span className="pill pill-ok" style={{ fontSize: 11, marginLeft: 'auto' }}>
              {b.direction === 'Dropoff' ? 'On the way home' : 'On the way to school'}
            </span>
          </div>

          {b.isLive && b.latitude != null && b.longitude != null ? (
            <>
              <MiniMap lat={b.latitude} lng={b.longitude} />

              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, color: 'var(--ok-ink, #1e7d4f)' }}>
                  ● Live — updated {b.positionAgeSeconds}s ago
                </span>

                {/* An honest accuracy warning. A 200m dot is not a bus. */}
                {b.accuracyMetres != null && b.accuracyMetres > 100 && (
                  <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                    (approximate — weak GPS)
                  </span>
                )}

                <a className="btn" style={{ marginLeft: 'auto', padding: '5px 12px', fontSize: 12.5 }}
                  href={`https://www.google.com/maps?q=${b.latitude},${b.longitude}`}
                  target="_blank" rel="noreferrer">
                  Open in Maps
                </a>
              </div>
            </>
          ) : (
            // We do NOT show a stale dot. A parent must never be allowed to believe
            // something untrue about where their child is.
            <div style={{ padding: '20px 0', textAlign: 'center' }}>
              <p style={{ fontSize: 14, color: 'var(--ink-2)' }}>
                The bus has left, but we've lost its signal.
              </p>
              <p style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 4 }}>
                This usually means the driver is in a low-coverage area. It should reappear shortly.
              </p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * A map without a map library. An OpenStreetMap tile behind a marker — no API key, no dependency,
 * no tracking pixels pointed at a page about children.
 */
function MiniMap({ lat, lng }: { lat: number; lng: number }) {
  // A small bounding box around the bus.
  const d = 0.004;
  const bbox = `${lng - d},${lat - d},${lng + d},${lat + d}`;
  const src = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat},${lng}`;

  return (
    <div style={{
      position: 'relative', width: '100%', height: 220,
      borderRadius: 'var(--r-md)', overflow: 'hidden', border: '1px solid var(--line)',
    }}>
      <iframe
        title="Where the bus is"
        src={src}
        style={{ width: '100%', height: '100%', border: 0 }}
        loading="lazy"
      />
    </div>
  );
}

/* ---------------------------------------------------------------- the pass */

function GatePassPanel() {
  const t = useT();
  const [passes, setPasses] = useState<GatePass[] | null>(null);
  const [children, setChildren] = useState<Child[]>([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api.get<GatePass[]>('/api/portal/gate-pass').then(setPasses).catch(() => setPasses([]));
    api.get<Child[]>('/api/portal/children').then((r) => setChildren(asList(r))).catch(() => setChildren([]));
  }, []);
  useEffect(load, [load]);

  // The code rotates every 30s, so we refetch. The pass ITSELF doesn't change — only the code does.
  //
  // ⚠ 4 SECONDS, NOT 15, WHILE A PASS IS LIVE.
  //
  // The old 15s interval also decided how long a pass stayed on screen AFTER the guard scanned it.
  // A parent whose child has already been collected kept holding up a live-looking QR for up to
  // fifteen seconds — long enough to hand the phone to someone else, long enough to try a second
  // gate, and long enough for the parent to think the scan failed and offer it again.
  //
  // A pass is on screen for a minute or two at a pickup, so polling four times more often costs
  // almost nothing and closes that window.
  useEffect(() => {
    const active = passes?.some((p) => p.status === 'Active');
    if (!active) return;
    const t = setInterval(load, 4_000);
    return () => clearInterval(t);
  }, [passes, load]);

  // ⚠ Refetch the moment the phone comes back to the foreground.
  //
  // A parent locks the screen while walking to the gate, or switches to WhatsApp. Browsers throttle
  // timers hard in a background tab — on iOS they can stop entirely — so the poll above cannot be
  // relied on to have run. This is what makes a collected pass disappear by the time they look
  // again, rather than several seconds later.
  useEffect(() => {
    const onWake = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onWake);
    return () => document.removeEventListener('visibilitychange', onWake);
  }, [load]);

  const active = passes?.filter((p) => p.status === 'Active') ?? [];
  const past = passes?.filter((p) => p.status !== 'Active').slice(0, 5) ?? [];

  // ⚠ A pass USED in the last few minutes — the one that just got scanned at the gate.
  //
  // Without this the QR simply disappears and the parent is shown "Collecting your child early?
  // Create a pass", as though nothing happened. Standing at the gate having just handed their
  // phone over, that reads as a failure: they create a SECOND pass, or ask the guard to scan
  // again. Confirming the collection is the whole point of the moment.
  const justUsed = passes?.find((p) =>
    p.status === 'Used' && p.usedAt && (Date.now() - new Date(p.usedAt).getTime()) < 5 * 60_000);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 12 }}>
        <h2 style={{ fontSize: 17, fontFamily: 'var(--font-display)' }}>{t('Pickup pass')}</h2>
        <button className="btn btn-accent" style={{ marginLeft: 'auto' }} onClick={() => setCreating(true)}>
          + Create pass
        </button>
      </div>

      {error && <div className="pill pill-danger" style={{ padding: '9px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>{error}</div>}

      {/* ⚠ CONFIRM THE COLLECTION. Shown in place of the QR the instant the gate accepts it. */}
      {active.length === 0 && justUsed && (
        <div className="card" style={{
          padding: 18, marginBottom: 12, textAlign: 'center',
          borderColor: 'var(--ok-ink, #1e7d4f)',
          background: 'color-mix(in srgb, #1e7d4f 8%, var(--surface))',
        }}>
          <div style={{ fontSize: 40, lineHeight: 1, marginBottom: 6 }}>✓</div>
          <strong style={{ fontSize: 16, color: 'var(--ok-ink, #1e7d4f)' }}>
            {justUsed.studentName} has been collected
          </strong>
          <div style={{ fontSize: 12.5, color: 'var(--ink-2)', marginTop: 6 }}>
            Checked at the gate{justUsed.usedAt
              ? ` at ${new Date(justUsed.usedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
              : ''}.
          </div>
          {/* ⚠ Says the pass is finished, in words. A parent who does not read this will try to
              show the same code again at a second gate and be refused. */}
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 8, lineHeight: 1.6 }}>
            This pass is now used and can’t be scanned again.
          </div>
        </div>
      )}

      {active.length === 0 && !justUsed ? (
        <div className="card" style={{ padding: 18 }}>
          <p style={{ fontSize: 13.5, color: 'var(--ink-2)' }}>
            Collecting your child early? Create a pass and show the code at the school gate.
          </p>
        </div>
      ) : active.length === 0 ? null : (
        active.map((p) => <PassCard key={p.id} pass={p} onRevoked={load} onError={setError} />)
      )}

      {past.length > 0 && (
        <>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', margin: '20px 0 8px' }}>
            Recent
          </div>
          {past.map((p) => (
            <div key={p.id} className="card" style={{ padding: 12, marginBottom: 6, display: 'flex', gap: 10, alignItems: 'center' }}>
              <span style={{ fontSize: 13 }}>{p.studentName}</span>
              <span className={`pill ${p.status === 'Used' ? 'pill-ok' : 'pill-muted'}`} style={{ fontSize: 11 }}>
                {p.status === 'Used' ? 'Collected' : p.status}
              </span>
              <span className="mono" style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ink-3)' }}>
                {p.usedAt ? formatDateTime(p.usedAt) : formatDate(p.issuedAt)}
              </span>
            </div>
          ))}
        </>
      )}

      {creating && (
        <CreatePassDialog children_={children}
          onClose={() => setCreating(false)}
          onDone={() => { setCreating(false); load(); }} />
      )}
    </div>
  );
}

function PassCard({ pass: p, onRevoked, onError }: {
  pass: GatePass; onRevoked: () => void; onError: (e: string) => void;
}) {
  const t = useT();
  const [countdown, setCountdown] = useState(p.secondsUntilRotate);

  // The ring counting down to the next rotation. It's not decoration — it tells the parent the
  // code is alive, and tells anyone watching over their shoulder that a photo is worthless.
  useEffect(() => {
    setCountdown(p.secondsUntilRotate);
    const t = setInterval(() => setCountdown((c) => (c > 0 ? c - 1 : 30)), 1000);
    return () => clearInterval(t);
  }, [p.secondsUntilRotate, p.currentCode]);

  const revoke = async () => {
    try { await api.post(`/api/portal/gate-pass/${p.id}/revoke`, {}); onRevoked(); }
    catch (e) { onError(e instanceof ApiError ? e.message : 'Could not cancel.'); }
  };

  // The QR encodes the pass id and rotating code, and NOTHING about the child. Photographed off a
  // phone it leaks nothing, and it is dead in thirty seconds anyway.
  //
  // ⚠ THE HYPHENS ARE STRIPPED FROM THE GUID, and that is not cosmetic.
  //
  // A v3-M byte-mode QR holds 42 bytes. `uuid:code` is 43 — one byte over, so this threw
  // "QR payload too long" and took the whole page down with it. The code's own comment claimed 43
  // characters would fit; the check said 42. It had never worked.
  //
  // Removing the four hyphens gives 39 bytes with room to spare, and loses nothing: a GUID's hyphens
  // are formatting, not data. The scanner re-inserts them, or parses the 32 hex characters directly.
  const payload = `${p.id.replace(/-/g, '')}:${p.currentCode}`;

  return (
    <div className="card" style={{ padding: 20, marginBottom: 12, textAlign: 'center' }}>
      <div style={{ marginBottom: 4 }}>
        <strong style={{ fontSize: 17, fontFamily: 'var(--font-display)' }}>{p.studentName}</strong>
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginBottom: 16 }}>
        {p.admissionNumber}{p.classLabel && ` · ${p.classLabel}`}
        {p.reason && ` · ${p.reason}`}
      </div>

      <QrCode value={payload} />

      {/* The code in large digits too — a guard's scanner may fail, and they can type it. */}
      <div className="mono" style={{
        fontSize: 30, letterSpacing: '0.18em', fontWeight: 700,
        margin: '16px 0 6px',
      }}>
        {p.currentCode.slice(0, 3)} {p.currentCode.slice(3)}
      </div>

      <div style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>
        Refreshes in {countdown}s · valid until {new Date(p.expiresAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </div>

      <p style={{ fontSize: 12, color: 'var(--ink-3)', margin: '12px 0 0', maxWidth: 300, marginInline: 'auto' }}>
        This code changes every 30 seconds. A screenshot won't work — show it live at the gate.
      </p>

      <button className="btn" style={{ marginTop: 14 }} onClick={revoke}>{t('Cancel this pass')}</button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * A QR code, drawn from scratch.
 *
 * Deliberately no library: adding a dependency to render 40 bytes onto a screen is not a trade worth
 * making, and this page is about children — the fewer third-party scripts on it, the better.
 *
 * This is a minimal byte-mode QR (version 3, EC level M) — enough for `uuid:code` (43 chars).
 */
function QrCode({ value }: { value: string }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const modules = buildQr(value);

    // Nothing drawn rather than a crash — the six-digit code beside it still opens the gate.
    if (!modules) return;

    const n = modules.length;
    const scale = Math.floor(220 / n);
    const size = n * scale;

    canvas.width = size;
    canvas.height = size;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, size, size);
    ctx.fillStyle = '#0A1220';

    for (let y = 0; y < n; y++) {
      for (let x = 0; x < n; x++) {
        if (modules[y][x]) ctx.fillRect(x * scale, y * scale, scale, scale);
      }
    }
  }, [value]);

  return (
    <div style={{ display: 'inline-block', padding: 10, background: '#fff', borderRadius: 12 }}>
      <canvas ref={canvasRef} />
    </div>
  );
}

/* ---- a small QR encoder (byte mode, version 3, EC level M) ---------------- */

function buildQr(text: string): boolean[][] | null {
  const data = new TextEncoder().encode(text);
  const VERSION = 3;               // 29x29
  const N = 17 + VERSION * 4;
  const TOTAL_CODEWORDS = 70;      // v3
  const EC_CODEWORDS = 26;         // level M
  const DATA_CODEWORDS = TOTAL_CODEWORDS - EC_CODEWORDS;   // 44

  if (data.length > DATA_CODEWORDS - 2) {
    // ⚠ RETURN NULL RATHER THAN THROW.
    //
    // This threw inside a render, which React treats as a component crash — so a payload one byte
    // too long took down the ENTIRE page and showed a parent a white screen.
    //
    // A QR that cannot be drawn is a real problem, but the six-digit code beside it still works and
    // the gate can be opened with that. Losing the page loses both.
    return null;
  }

  // ---- bitstream ----
  const bits: number[] = [];
  const push = (val: number, len: number) => {
    for (let i = len - 1; i >= 0; i--) bits.push((val >>> i) & 1);
  };

  push(0b0100, 4);                 // byte mode
  push(data.length, 8);            // char count (v1-9 byte mode = 8 bits)
  for (const b of data) push(b, 8);
  push(0, 4);                      // terminator

  while (bits.length % 8 !== 0) bits.push(0);

  const codewords: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    let b = 0;
    for (let j = 0; j < 8; j++) b = (b << 1) | bits[i + j];
    codewords.push(b);
  }
  // pad
  const PAD = [0xEC, 0x11];
  let pi = 0;
  while (codewords.length < DATA_CODEWORDS) codewords.push(PAD[pi++ % 2]);

  // ---- Reed-Solomon ----
  const ec = rsEncode(codewords, EC_CODEWORDS);
  const all = [...codewords, ...ec];

  // ---- matrix ----
  const m: (boolean | null)[][] = Array.from({ length: N }, () => Array<boolean | null>(N).fill(null));

  const finder = (r: number, c: number) => {
    for (let y = -1; y <= 7; y++) {
      for (let x = -1; x <= 7; x++) {
        const yy = r + y, xx = c + x;
        if (yy < 0 || yy >= N || xx < 0 || xx >= N) continue;
        const on =
          (y >= 0 && y <= 6 && (x === 0 || x === 6)) ||
          (x >= 0 && x <= 6 && (y === 0 || y === 6)) ||
          (y >= 2 && y <= 4 && x >= 2 && x <= 4);
        m[yy][xx] = on;
      }
    }
  };
  finder(0, 0); finder(0, N - 7); finder(N - 7, 0);

  // alignment pattern (v3 → centre 22,22)
  for (let y = -2; y <= 2; y++) {
    for (let x = -2; x <= 2; x++) {
      m[22 + y][22 + x] = Math.max(Math.abs(x), Math.abs(y)) !== 1;
    }
  }

  // timing
  for (let i = 8; i < N - 8; i++) {
    m[6][i] = i % 2 === 0;
    m[i][6] = i % 2 === 0;
  }
  m[N - 8][8] = true;   // dark module

  // reserve format areas
  for (let i = 0; i < 9; i++) {
    if (m[8][i] === null) m[8][i] = false;
    if (m[i][8] === null) m[i][8] = false;
  }
  for (let i = 0; i < 8; i++) {
    if (m[8][N - 1 - i] === null) m[8][N - 1 - i] = false;
    if (m[N - 1 - i][8] === null) m[N - 1 - i][8] = false;
  }

  // ---- place data, mask 0 ----
  let bitIdx = 0;
  const dataBits: number[] = [];
  for (const cw of all) for (let i = 7; i >= 0; i--) dataBits.push((cw >>> i) & 1);

  let upward = true;
  for (let col = N - 1; col > 0; col -= 2) {
    if (col === 6) col--;   // skip timing column
    for (let i = 0; i < N; i++) {
      const row = upward ? N - 1 - i : i;
      for (let c = 0; c < 2; c++) {
        const cc = col - c;
        if (m[row][cc] !== null) continue;
        let bit = bitIdx < dataBits.length ? dataBits[bitIdx++] === 1 : false;
        if ((row + cc) % 2 === 0) bit = !bit;   // mask 0
        m[row][cc] = bit;
      }
    }
    upward = !upward;
  }

  // ---- format info (EC M, mask 0) ----
  const FORMAT = 0b101010000010010;

  // ⚠ MSB-FIRST. This read the bits LSB-first (`FORMAT >> i`), which inverts the format field.
  //
  // The symbol still DRAWS — it looks like a perfectly normal QR — but the format field carries the
  // error-correction level and mask pattern, so a scanner cannot decode it and simply ignores the
  // code. That is the "I scanned it and nothing happened" bug: not a camera fault, not a network
  // fault, an unreadable symbol that looks fine to a human.
  //
  // Verified against a reference encoder: at m[8][0..5] the spec gives 101010 and this was
  // producing 010010.
  const fbit = (i: number) => ((FORMAT >> (14 - i)) & 1) === 1;

  for (let i = 0; i <= 5; i++) m[8][i] = fbit(i);
  m[8][7] = fbit(6);
  m[8][8] = fbit(7);
  m[7][8] = fbit(8);
  for (let i = 9; i <= 14; i++) m[14 - i][8] = fbit(i);

  for (let i = 0; i <= 7; i++) m[N - 1 - i][8] = fbit(i);

  // ⚠ RESTORE THE DARK MODULE. The loop above writes m[N-1-i][8] for i=0..7, and at i=7 that is
  // m[N-8][8] — the fixed dark module the spec requires to be black. Setting it earlier is not
  // enough; it must be re-set AFTER the format bits, or the symbol carries one wrong module in a
  // position decoders check.
  m[N - 8][8] = true;
  for (let i = 8; i <= 14; i++) m[8][N - 15 + i] = fbit(i);

  // quiet zone
  const Q = 4;
  const out: boolean[][] = Array.from({ length: N + Q * 2 }, () => Array<boolean>(N + Q * 2).fill(false));
  for (let y = 0; y < N; y++)
    for (let x = 0; x < N; x++)
      out[y + Q][x + Q] = m[y][x] === true;

  return out;
}

/** GF(256) Reed-Solomon, the standard QR generator polynomial. */
function rsEncode(data: number[], ecLen: number): number[] {
  const EXP = new Uint8Array(512);
  const LOG = new Uint8Array(256);
  let x = 1;
  for (let i = 0; i < 255; i++) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11D;
  }
  for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255];

  const mul = (a: number, b: number) => (a === 0 || b === 0 ? 0 : EXP[LOG[a] + LOG[b]]);

  // generator polynomial
  let gen = [1];
  for (let i = 0; i < ecLen; i++) {
    const next = new Array(gen.length + 1).fill(0);
    for (let j = 0; j < gen.length; j++) {
      next[j] ^= gen[j];
      next[j + 1] ^= mul(gen[j], EXP[i]);
    }
    gen = next;
  }

  const rem = new Array(ecLen).fill(0);
  for (const d of data) {
    const factor = d ^ rem[0];
    rem.shift();
    rem.push(0);
    for (let j = 0; j < ecLen; j++) rem[j] ^= mul(gen[j + 1], factor);
  }
  return rem;
}

/* -------------------------------------------------------------------------- */

function CreatePassDialog({ children_, onClose, onDone }: {
  children_: Child[]; onClose: () => void; onDone: () => void;
}) {
  const t = useT();
  const [studentId, setStudentId] = useState(children_.length === 1 ? children_[0].id : '');
  const [reason, setReason] = useState('');
  const [minutes, setMinutes] = useState(60);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!studentId) return;
    setBusy(true); setError(null);
    try {
      await api.post('/api/portal/gate-pass', {
        studentId, reason: reason.trim() || null, validMinutes: minutes,
      });
      onDone();
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Could not create the pass.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <h2 className="h-display" style={{ fontSize: 20, marginBottom: 4 }}>{t('Create a pickup pass')}</h2>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginBottom: 16 }}>
          Show the code at the gate. It changes every 30 seconds, so it can't be screenshotted or reused.
        </p>

        {error && <div className="pill pill-danger" style={{ padding: '9px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>{error}</div>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* ⚠ A parent with NO linked child previously saw this form with a permanently disabled
              button and no explanation — a dead end with nothing to click and nothing to read.
              Say what is wrong and what to do about it. */}
          {children_.length === 0 && (
            <div className="pill pill-danger" style={{ padding: '10px 12px', borderRadius: 8, justifyContent: 'flex-start' }}>
              {t('No child is linked to your account yet, so a pickup pass cannot be created. Please ask the school office to link your child.')}
            </div>
          )}
          {children_.length > 1 && (
            <div>
              <label className="label">{t('Collecting')}</label>
              <select className="input" value={studentId} onChange={(e) => setStudentId(e.target.value)}>
                <option value="">Select…</option>
                {children_.map((c) => <option key={c.id} value={c.id}>{c.fullName}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="label">Reason (shown to the guard)</label>
            <input className="input" placeholder="Doctor's appointment"
              value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <div>
            <label className="label">{t('Valid for')}</label>
            <select className="input" value={minutes} onChange={(e) => setMinutes(Number(e.target.value))}>
              <option value={30}>30 minutes</option>
              <option value={60}>1 hour</option>
              <option value={180}>3 hours</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
          <button className="btn" onClick={onClose}>{t('Cancel')}</button>
          <button className="btn btn-accent" disabled={busy || !studentId} onClick={save} style={{ marginLeft: 'auto' }}>
            {busy ? 'Creating…' : 'Create pass'}
          </button>
        </div>
      </div>
    </div>
  );
}
